import { useState } from "react";
import { ReasonCancelled, isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-2
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { financeApi, formatCents, PAYABLE_KIND_LABEL, type Payable, type PayableKind } from "@/services/finance";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";
import { Can } from "@/auth/permissions";

type KindTab = "all" | PayableKind;
type StatusTab = "all" | "pending_approval" | "approved" | "paid";

const KIND_TABS: { key: KindTab; icon: string; label: string }[] = [
  { key: "all", icon: "📋", label: "全部" },
  { key: "hotel", icon: "🏨", label: "酒店" },
  { key: "vehicle", icon: "🚐", label: "车辆" },
  { key: "driver", icon: "👤", label: "司导" },
  { key: "attraction", icon: "🎫", label: "景点" },
];
const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: "all", label: "全部状态" },
  { key: "pending_approval", label: "待审" },
  { key: "approved", label: "待支付" },
  { key: "paid", label: "已支付" },
];
const KIND_CHIP: Record<PayableKind, string> = {
  hotel: "text-blue-500", vehicle: "text-emerald-500", driver: "text-amber-500", attraction: "text-violet-600", other: "text-slate-500",
};
const STATUS_CN: Record<string, string> = { pending_approval: "待老板审", approved: "待支付", rejected: "已驳回", paid: "已支付" };

/** 财务 · 应付账款 Payables（/admin/finance/payables · canvas page-73）。 */
export function PayablesPage() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  // 资金告警面板「应付账款待审」深链 ?status=pending_approval → 落地即筛待审
  const [kind, setKind] = useState<KindTab>("all");
  const [status, setStatus] = useState<StatusTab>(() => {
    const s = params.get("status");
    return s === "pending_approval" || s === "approved" || s === "paid" ? s : "all";
  });

  const allQ = useQuery({ queryKey: ["finance", "payables", "all"], queryFn: () => financeApi.payables() });
  const all = allQ.data ?? [];

  // E 跨页联动：应付动作 → 总览待付KPI(overview)+总览告警ap_due(dashboard) 都跟着变
  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["finance", "payables"] });
    qc.invalidateQueries({ queryKey: ["finance", "overview"] });
    qc.invalidateQueries({ queryKey: ["finance", "dashboard"] });
  };
  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();   // REASON-2 · 应付审批原因：拒绝必填、批准选填
  const decide = useMutation({
    mutationFn: async ({ p, approve }: { p: Payable; approve: boolean }) => {
      const r = await askReason({
        scene: "payable_decision", group: approve ? "approve" : "reject", required: !approve, danger: !approve,
        title: approve ? "批准应付" : "拒绝应付", subtitle: formatCents(p.amount_cents, p.currency), confirmText: approve ? "确认批准" : "确认拒绝",
      });
      if (!r) throw new ReasonCancelled();
      return financeApi.decidePayable(p.id, approve, r);
    },
    onSuccess: invalidateAll,
    onError: (e) => { if (!isReasonCancelled(e)) alert((e as Error).message || "操作失败"); },
  });
  const submit = useMutation({
    mutationFn: (p: Payable) => financeApi.submitPayableApproval(p.id),
    onSuccess: invalidateAll,
    onError: (e) => alert((e as Error).message || "提交失败"),
  });

  // KPI 聚合
  const pending = all.filter((p) => p.status === "pending_approval");
  const apTotal = all.filter((p) => p.status !== "paid").reduce((s, p) => s + p.amount_cents, 0);
  const paidTotal = all.filter((p) => p.status === "paid").reduce((s, p) => s + p.amount_cents, 0);
  const overdue = all.filter((p) => p.status !== "paid" && new Date(p.due_at).getTime() < Date.now());
  const kindCount = (k: KindTab) => (k === "all" ? all.length : all.filter((p) => p.kind === k).length);
  const ccy = (p: Payable) => formatCents(p.amount_cents, p.currency);  // 币种感知·去 USD$ 双币种

  const rows = all
    .filter((p) => kind === "all" || p.kind === kind)
    .filter((p) => status === "all" || p.status === status);

  const kpis = [
    { label: "本月应付", value: formatCents(apTotal), sub: `${all.filter((p) => p.status !== "paid").length} 条待处理`, color: "text-red-500" },
    { label: "已支付", value: formatCents(paidTotal), sub: "本月支出", color: "text-emerald-500" },
    { label: "待老板审", value: String(pending.length), sub: "🔴 Q3A 全部审", color: "text-amber-500" },
    { label: "逾期", value: formatCents(overdue.reduce((s, p) => s + p.amount_cents, 0)), sub: overdue.length ? `🔴 ${overdue.length} 条` : "良性", color: overdue.length ? "text-red-500" : "text-emerald-500" },
  ];

  if (allQ.isLoading) return <LoadingState />;
  if (allQ.isError) return (
    <div className="flex flex-col items-center justify-center gap-2 p-12">
      <p className="text-[13px] font-semibold text-red-700">应付账款加载失败</p>
      <p className="text-[11px] text-red-500">{(allQ.error as Error)?.message || "网络错误"}</p>
      <button onClick={() => allQ.refetch()} disabled={allQ.isFetching} className="mt-1 rounded-md bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">{allQ.isFetching ? "重试中…" : "点击重试"}</button>
    </div>
  );

  return (
    <PageShell
      title="💸 应付账款 · Payables"
      subtitle="5 tab 分类 · Q3A 现阶段所有支出必经老板审 · 稳定后改分层"
      actions={<Can perm="finance.payables.submit"><button className="flex h-9 items-center rounded-lg bg-blue-600 px-3 text-[12px] font-semibold text-white hover:bg-blue-700">批量提交付款</button></Can>}
    >
      <div className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800">🔒 仅老板 + 财务可见 · 经理无访问权限</div>

      {/* 4 KPI */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[11px] font-semibold text-slate-500">{k.label}</div>
            <div className={`mt-1.5 text-[22px] font-bold leading-none ${k.color}`}>{k.value}</div>
            <div className="mt-1.5 text-[10px] text-slate-400">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* 区3 · 🔴 待老板审批支出 */}
      <Card title="🔴 待老板审批支出（Q3A · 现阶段全部审 · 稳定后切换分层）" action={<span className="text-[12px] text-slate-500">财务提交 → 老板审 → 通过即可支付</span>}>
        {allQ.isLoading ? <LoadingState /> : pending.length === 0 ? (
          <div className="py-6 text-center text-[13px] text-slate-400">🟢 暂无待审批支出</div>
        ) : (
          <div className="flex flex-col gap-3">
            {pending.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-slate-200 px-4 py-3">
                <div className="w-40 shrink-0">
                  <div className="text-[11px] font-bold text-amber-800">{p.invoice_ref}</div>
                  <div className="text-[10px] text-amber-600">{new Date(p.submitted_at).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
                  <div className="text-[11px] font-semibold text-slate-900">财务提交</div>
                </div>
                <span className={`text-[10px] font-bold ${KIND_CHIP[p.kind]}`}>{PAYABLE_KIND_LABEL[p.kind]}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold text-slate-900">{p.supplier} · {p.payment_terms ?? ""} · {ccy(p)}</div>
                  {p.detail && <div className="text-[10px] text-slate-500">{p.detail}</div>}
                </div>
                <Can perm="finance.payables.approve"><div className="flex shrink-0 gap-1.5">
                  <button onClick={() => decide.mutate({ p, approve: false })} disabled={decide.isPending} className="rounded-md border border-slate-300 px-2 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">驳回</button>
                  <button onClick={() => decide.mutate({ p, approve: true })} disabled={decide.isPending} className="rounded-md bg-blue-600 px-2 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">批准付款</button>
                </div></Can>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 区4 · 全部列表 */}
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-4 border-b border-slate-100 pb-3">
          {KIND_TABS.map((t) => (
            <button key={t.key} onClick={() => setKind(t.key)} className={`text-[13px] ${kind === t.key ? "font-bold text-blue-600" : "font-medium text-slate-500 hover:text-slate-700"}`}>{t.icon} {t.label} {kindCount(t.key)}</button>
          ))}
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {STATUS_TABS.map((t) => (
            <button key={t.key} onClick={() => setStatus(t.key)} className={`h-8 rounded-lg px-3 text-[12px] ${status === t.key ? "bg-blue-600 font-semibold text-white" : "font-medium text-slate-700 hover:bg-slate-100"}`}>{t.label}</button>
          ))}
          <span className="ml-auto text-[11px] text-slate-500">共 {rows.length} 条</span>
        </div>
        {allQ.isLoading ? <LoadingState /> : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-[13px]">
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3"><span className={`text-[10px] font-bold ${KIND_CHIP[p.kind]}`}>{PAYABLE_KIND_LABEL[p.kind]}</span></td>
                    <td className="px-4 py-3">
                      <div className="text-[12px] font-semibold text-slate-900">{p.supplier}</div>
                      <div className="text-[10px] text-slate-500">{p.invoice_ref}{p.payment_terms ? ` · ${p.payment_terms}` : ""}</div>
                    </td>
                    <td className="px-4 py-3"><div className="text-[14px] font-bold text-red-500">{ccy(p)}</div><div className="text-[9px] text-slate-400">{p.currency}</div></td>
                    <td className="px-4 py-3"><div className="text-[11px] font-semibold text-slate-900">{new Date(p.due_at).toLocaleDateString("zh-CN")}</div><div className="text-[9px] text-slate-400">到期日</div></td>
                    <td className="px-4 py-3 text-[10px] font-bold text-amber-500">{STATUS_CN[p.status] ?? p.status}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => alert(`${p.supplier}\n${p.invoice_ref} · ${ccy(p)}\n${p.detail ?? ""}`)} className="rounded-md border border-slate-300 px-2 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">详情</button>
                        {p.status === "pending_approval" && <Can perm="finance.payables.submit"><button onClick={() => submit.mutate(p)} disabled={submit.isPending} className="rounded-md bg-blue-600 px-2 py-1 text-[12px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">提交付款</button></Can>}
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td className="px-4 py-6 text-center text-slate-400">该分类/状态下暂无应付</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    {reasonDialog}
    </PageShell>
  );
}
